// src/components/themes/default/store/branch/BranchCartDrawer.tsx
"use client";

import { ShoppingBag, X, Plus, Minus, Trash2, ArrowRight, Package } from "lucide-react";
import { useBranchCart } from "./BranchCartContext";

interface BranchCartDrawerProps {
  branchName: string;
  primaryColor: string;
}

export default function BranchCartDrawer({ branchName, primaryColor }: BranchCartDrawerProps) {
  const {
    cart,
    isCartOpen,
    setIsCartOpen,
    setIsCheckoutOpen,
    updateQuantity,
    removeFromCart,
    cartTotal,
  } = useBranchCart();

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-xs animate-fadeIn">
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col justify-between animate-slideInRight">
        {/* Drawer Header */}
        <div className="p-4 sm:p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div
              style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
              className="p-2 rounded-xl"
            >
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900">আপনার ব্রাঞ্চ কার্ট</h3>
              <p className="text-[10px] text-slate-500 font-bold">{branchName} Outlet</p>
            </div>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cart.length === 0 ? (
            <div className="py-24 text-center space-y-2">
              <ShoppingBag className="w-12 h-12 mx-auto text-slate-300" />
              <p className="text-xs font-bold text-slate-400">আপনার কার্ট বর্তমানে খালি আছে।</p>
            </div>
          ) : (
            cart.map((item) => (
              <div
                key={item.id}
                className="p-3 rounded-2xl border border-slate-200 bg-slate-50/60 flex items-center gap-3"
              >
                <div className="w-14 h-14 rounded-xl bg-white border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <Package className="w-6 h-6 text-slate-300" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-black text-slate-900 truncate">{item.name}</h4>
                  <p className="text-xs font-mono font-bold text-emerald-600 mt-0.5">
                    ৳{item.price.toLocaleString()}
                  </p>

                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      className="w-6 h-6 rounded-lg bg-white border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 cursor-pointer"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="text-xs font-bold font-mono px-1">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      className="w-6 h-6 rounded-lg bg-white border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 cursor-pointer"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => removeFromCart(item.id)}
                  className="p-2 text-slate-400 hover:text-rose-500 cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Drawer Footer */}
        {cart.length > 0 && (
          <div className="p-4 sm:p-5 border-t border-slate-200 bg-slate-50 space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-slate-600">
              <span>মোট বিল:</span>
              <span className="text-base font-black font-mono text-emerald-600">
                ৳{cartTotal.toLocaleString()}
              </span>
            </div>

            <button
              onClick={() => {
                setIsCartOpen(false);
                setIsCheckoutOpen(true);
              }}
              style={{ backgroundColor: primaryColor }}
              className="w-full py-3 rounded-2xl text-xs font-black text-white shadow-lg flex items-center justify-center gap-2 cursor-pointer hover:opacity-95 active:scale-95"
            >
              <span>অর্ডার সম্পন্ন করুন (Checkout)</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}